import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { User } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { getTeamsLabel, getUserEditPath, useUserOptions } from './user-options'
import { useUsersApi } from '../api/list.hook'
import { useUsersStore } from './store'

interface TableUsersReturn {
  users: ComputedRef<User[]>
  isLoading: ComputedRef<boolean>
  hasUsers: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  formatDateTime: (value: string | null) => string
  getRoles: (user: User) => string
  getStatus: (user: User) => string
  getTeams: (user: User) => string
  openUser: (user: User) => Promise<void>
  askDelete: (user: User) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableUsers = (): TableUsersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useUsersStore()
  const dayjs = useDayjs()
  const { getRoleLabels, getStatusLabel } = useUserOptions()
  const { canManageUsers } = useSidebarPrivileges()

  const users = computed<User[]>(() => store.getUsers)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasUsers = computed<boolean>(() => store.hasUsers)
  const canManage = computed<boolean>(() => canManageUsers.value)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const getRoles = (user: User): string => getRoleLabels(user.roles)
  const getStatus = (user: User): string => getStatusLabel(user.status)
  const getTeams = (user: User): string => getTeamsLabel(user.teams)

  const openUser = async (user: User): Promise<void> => {
    await router.push(getUserEditPath(user.id))
  }

  const askDelete = (user: User): void => {
    store.setUserToDelete(user)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await useUsersApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
    await useUsersApi()
  }

  return {
    users,
    isLoading,
    hasUsers,
    canManage,
    meta,
    perPageOptions,
    formatDateTime,
    getRoles,
    getStatus,
    getTeams,
    openUser,
    askDelete,
    setChangePage,
    setChangePerPage,
  }
}
